import React, { useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Send, Mail, MessageSquare, Bell } from 'lucide-react';

// Dialog to publish a date-range schedule and notify assigned staff via Email/SMS/in-app.
export function CalendarPublishDialog({ open, onClose, dateRange, shifts, onPublished }) {
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [sendInApp, setSendInApp] = useState(true);
  const [customMessage, setCustomMessage] = useState('');
  const [publishing, setPublishing] = useState(false);

  // Compute affected staff directly from shifts (avoids relying on staff prop coverage)
  const affectedStaff = useMemo(() => {
    const map = new Map();
    shifts.forEach(s => {
      if (['DRAFT', 'OPEN', 'ASSIGNED', 'CONFIRMED'].includes(s.status)) {
        (s.assigned_staff || []).forEach(a => {
          if (a?.id && !map.has(a.id)) map.set(a.id, a.name || 'Staff');
        });
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [shifts]);

  const totalShifts = useMemo(() =>
    shifts.filter(s => ['DRAFT', 'OPEN', 'ASSIGNED', 'CONFIRMED'].includes(s.status) && (s.assigned_staff?.length || 0) > 0).length
  , [shifts]);

  const handlePublish = async () => {
    if (!sendEmail && !sendSms && !sendInApp) {
      toast.error('Select at least one channel');
      return;
    }
    setPublishing(true);
    try {
      const res = await api.post('/roster/publish-schedule', {
        start_date: dateRange.start,
        end_date: dateRange.end,
        send_email: sendEmail,
        send_sms: sendSms,
        send_in_app: sendInApp,
        custom_message: customMessage || null,
      });
      const d = res.data;
      toast.success(`Published to ${d.staff_notified} staff · ${d.email_sent} emails · ${d.sms_sent} SMS · ${d.in_app_sent} in-app`);
      onPublished?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to publish schedule');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="publish-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-4 h-4" /> Publish Schedule</DialogTitle>
          <DialogDescription>Notify staff of their assigned shifts for this period</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-slate-50 rounded-md p-3 text-sm">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Date range</span>
              <span className="text-xs font-medium">{dateRange.start} → {dateRange.end}</span>
            </div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Staff to notify</span>
              <span className="text-xs font-medium">{affectedStaff.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Shifts assigned</span>
              <span className="text-xs font-medium">{totalShifts}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium">Channels</Label>
            <div className="flex items-start gap-2">
              <Checkbox id="ch-email" checked={sendEmail} onCheckedChange={setSendEmail} data-testid="publish-email-toggle" />
              <label htmlFor="ch-email" className="text-xs cursor-pointer flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email digest with shift details</label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox id="ch-sms" checked={sendSms} onCheckedChange={setSendSms} data-testid="publish-sms-toggle" />
              <label htmlFor="ch-sms" className="text-xs cursor-pointer flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> SMS short notification</label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox id="ch-app" checked={sendInApp} onCheckedChange={setSendInApp} data-testid="publish-inapp-toggle" />
              <label htmlFor="ch-app" className="text-xs cursor-pointer flex items-center gap-1.5"><Bell className="w-3 h-3" /> In-app notification</label>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Custom message (optional)</Label>
            <Textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)} rows={3} className="text-xs"
              placeholder="e.g. Reminder: Please confirm your shifts by Sunday."
              data-testid="publish-custom-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={publishing}>Cancel</Button>
          <Button onClick={handlePublish} disabled={publishing || affectedStaff.length === 0} data-testid="publish-schedule-submit">
            {publishing ? 'Publishing...' : <><Send className="w-3.5 h-3.5 mr-1" /> Publish & Notify</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
